import {LOCALES} from '../i18n/locales';
import enMessages from '../i18n/messages/en-US';
import frMessages from '../i18n/messages/fr-FR';
import errorCatalog from '../page/messages/error/messages';
import {store} from '../app/store';
import {pushErrorToast, ErrorToastPayload} from '../features/error/errorToastSlice';
import {hasActiveSession} from './axios_helpers';

/**
 * ============================================================================
 * Backend exception module (CustomException handling)
 * ============================================================================
 *
 * The backend microservices (gateway/core-service) expose every error through
 * a uniform JSON contract. A `CustomException(ErrorCode.BAD_REQUEST, "EMPRUNT_ID_MUST_BE_NOT_NULL")`
 * is serialized as:
 *
 *   {
 *     "status":       400,
 *     "message":      "EMPRUNT_ID_MUST_BE_NOT_NULL",   // business TAG (2nd ctor arg)
 *     "codeMessage":  "BAD_REQUEST",                   // ErrorCode family
 *     "error":        "BAD_REQUEST",                   // http status name
 *     "timestamp":    "...",
 *     "errorDetails": []
 *   }
 *
 * This module is the single place that turns such an error into a FRIENDLY,
 * user-facing, language-aware message:
 *   - the TAG (`message`) is looked up first (e.g. EMPRUNT_ID_MUST_BE_NOT_NULL),
 *   - then the `codeMessage` family (BAD_REQUEST, NOT_FOUND, ...),
 *   - then the HTTP status,
 *   - and finally a generic localized fallback — a technical payload is never
 *     shown to the end user.
 *
 * ----------------------------------------------------------------------------
 * HOW TO ADD A NEW BACKEND ERROR TAG (maintainability contract)
 * ----------------------------------------------------------------------------
 * When the backend adds a new `CustomException(ErrorCode.X, "MY_NEW_TAG")`:
 *
 *  1. Add the friendly text in BOTH i18n catalogs under the Errors scope,
 *     with the key `boilerplate.components.Errors.MY_NEW_TAG`:
 *        - src/i18n/messages/en-US.tsx
 *        - src/i18n/messages/fr-FR.tsx
 *  2. (Optional, for documentation/indexing) register the TAG in
 *     src/page/messages/error/messages.tsx
 *
 * That is all. The interceptor + this module pick the new TAG up
 * automatically — no per-page code is required.
 *
 * The displayed language follows the language chosen by the user
 * (`user.lang` in the redux store, same store that drives react-intl).
 * ============================================================================
 */

const ERRORS_SCOPE = 'boilerplate.components.Errors';

// Per-locale flattened catalogs (same files react-intl uses).
const catalogs: Record<string, Record<string, string>> = {
    [LOCALES.ENGLISH]: (enMessages as any)[LOCALES.ENGLISH] ?? {},
    [LOCALES.FRENCH]: (frMessages as any)[LOCALES.FRENCH] ?? {},
};

const isEnglishLocale = (locale: string): boolean => locale === LOCALES.ENGLISH;

/** Language currently chosen by the end user (defaults to French, like the app). */
const currentLocale = (): string => {
    try {
        const lang: string = (store.getState() as any)?.user?.lang;
        return isEnglishLocale(lang) ? LOCALES.ENGLISH : LOCALES.FRENCH;
    } catch (e) {
        return LOCALES.FRENCH;
    }
};

const hasText = (locale: string, messageId: string): boolean =>
    Boolean(catalogs[locale] && catalogs[locale][messageId]);

/** True when a message id is translated in the current user language. */
const translatedInCurrentLocale = (messageId: string): string | undefined =>
    hasText(currentLocale(), messageId) ? messageId : undefined;

/**
 * A backend business token is an UPPER_SNAKE string with no space
 * (e.g. "EMPRUNT_ID_MUST_BE_NOT_NULL", "DELETE_NOT_PERMIT_FOR_THIS_APPOINTMENT").
 */
export const isBackendToken = (value: any): boolean =>
    typeof value === 'string' && /^[A-Z][A-Z0-9_]*$/.test(value);

const fallbackKeyFor = (token: string): string | undefined => {
    if (token.endsWith('_NOT_ALLOW_FOR_CANCEL_RDV')
        || token.endsWith('_NOT_ALLOW_FOR_CONFIRM_RDV')
        || token.endsWith('_NOT_ALLOW')) {
        return 'ACTION_NOT_ALLOWED';
    }
    if (token.startsWith('PRESET_CHOICE_EXCEED_')) {
        return 'PRESET_CHOICE_EXCEEDED';
    }
    return undefined;
};

/**
 * Resolve a single backend token (TAG or codeMessage) to an i18n message id
 * that actually has a translation in the current user language.
 */
const resolveTokenId = (token: string): string | undefined => {
    if (!isBackendToken(token)) {
        return undefined;
    }
    const candidates: string[] = [];
    // 1. Explicit registry entry (allows aliasing / non-identity mappings).
    const registered = errorCatalog[token];
    if (registered) {
        candidates.push(registered);
    }
    // 2. Auto-derived conventional key.
    candidates.push(`${ERRORS_SCOPE}.${token}`);
    // 3. Wildcard fallback for status-bearing tokens (…_NOT_ALLOW, PRESET_CHOICE_EXCEED_*).
    const wildcardKey = fallbackKeyFor(token);
    if (wildcardKey && wildcardKey !== token) {
        const registeredWildcard = errorCatalog[wildcardKey];
        if (registeredWildcard) {
            candidates.push(registeredWildcard);
        }
        candidates.push(`${ERRORS_SCOPE}.${wildcardKey}`);
    }
    for (const candidate of candidates) {
        const translated = translatedInCurrentLocale(candidate);
        if (translated) {
            return translated;
        }
    }
    return undefined;
};

/** Generic localized id used when the backend only gives us an HTTP status. */
const HTTP_STATUS_TOKEN: Record<number, string> = {
    400: 'BAD_REQUEST',
    401: 'BAD_CREDENTIALS',
    403: 'FORBIDEN_ACCESS',
    404: 'NOT_FOUND',
    406: 'ILLEGAL_ARGUMENT',
    408: 'REQUEST_TIMEOUT',
    409: 'CONSTRAINT_VIOLATION',
    410: 'GONE_EXCEPTION',
    412: 'DATA_LOCKED',
    415: 'UNSUPPORTED_MEDIA_TYPE',
    424: 'SIZE_EXCEPTION',
    503: 'SERVICE_UNAVAILABLE',
};

const resolveStatusId = (status: number): string | undefined => {
    const token = HTTP_STATUS_TOKEN[status];
    return token ? resolveTokenId(token) : undefined;
};

const GENERIC_FALLBACK_ID = `${ERRORS_SCOPE}.DEFAULT_ERROR`;
const NETWORK_ERROR_ID = `${ERRORS_SCOPE}.ERR_NETWORK`;
const TIMEOUT_ERROR_ID = `${ERRORS_SCOPE}.REQUEST_TIMEOUT`;

/**
 * Requests that deliberately opt out of the automatic error toast.
 * The login page displays its error inline next to the form.
 */
const isOptOutUrl = (url: string | undefined): boolean => {
    if (!url) {
        return false;
    }
    // The login page and the username-rename form display their errors inline.
    return url.includes('/public/auth/login')
        || url.includes('/security/update/username');
};

/**
 * Turn any axios error into a localized, user-friendly toast payload.
 * Returns `null` when the error must stay silent (canceled request, opt-out
 * URL, session-expiry redirect already handled by the axios interceptor…).
 */
export const resolveBackendError = (error: any): ErrorToastPayload | null => {
    // Aborted/canceled requests (e.g. unmounted components) must stay silent.
    if (error?.code === 'ERR_CANCELED') {
        return null;
    }
    // Session-expiry (401 while a session is active) is handled by the
    // interceptor, which logs the user out and redirects to "/" — no toast here.
    if (error?.response?.status === 401 && hasActiveSession()) {
        return null;
    }
    if (isOptOutUrl(error?.config?.url)) {
        return null;
    }

    // ---- No HTTP response: network / timeout -------------------------------
    if (!error?.response) {
        if (error?.code === 'ECONNABORTED') {
            return {messageId: translatedInCurrentLocale(TIMEOUT_ERROR_ID) ?? GENERIC_FALLBACK_ID};
        }
        // Any other transport failure (ERR_NETWORK, CORS, DNS...) is a
        // connectivity problem for the end user.
        const messageId = translatedInCurrentLocale(NETWORK_ERROR_ID);
        return messageId ? {messageId} : {messageId: GENERIC_FALLBACK_ID};
    }

    // ---- HTTP response with backend payload --------------------------------
    const data: any = error?.response?.data ?? {};
    const status: number = error?.response?.status ?? 0;
    const messageToken: any = data?.message;
    const codeMessageToken: any = data?.codeMessage;

    // 1. Business TAG carried by CustomException (e.g. EMPRUNT_ID_MUST_BE_NOT_NULL).
    if (isBackendToken(messageToken)) {
        const messageId = resolveTokenId(messageToken);
        if (messageId) {
            return {messageId};
        }
    }
    // 2. ErrorCode family (e.g. BAD_REQUEST, NOT_FOUND, CONSTRAINT_VIOLATION…).
    if (isBackendToken(codeMessageToken)) {
        const messageId = resolveTokenId(codeMessageToken);
        if (messageId) {
            return {messageId};
        }
    }
    // 3. HTTP status fallback.
    const statusId = resolveStatusId(status);
    if (statusId) {
        return {messageId: statusId};
    }
    // 4. Last resort: generic localized message — never a raw technical string.
    return {messageId: GENERIC_FALLBACK_ID};
};

/**
 * Push a backend error into the global snackbar queue. This is invoked
 * automatically by the axios response interceptor (see axios_helpers.tsx),
 * so application code does not need to handle errors by itself anymore.
 */
export const notifyBackendError = (error: any): void => {
    const payload = resolveBackendError(error);
    if (payload) {
        store.dispatch(pushErrorToast(payload));
    }
};

/** Plain localized text of an i18n message id, for the current user language. */
export const localizedText = (messageId: string): string => {
    const locale = currentLocale();
    return (catalogs[locale] && catalogs[locale][messageId]) || messageId;
};

/**
 * For the rare inline contexts (login form, dialogs…) that need the message
 * as a string instead of a snackbar. Never returns a technical payload.
 */
export const getLocalizedErrorMessage = (error: any): string => {
    const payload = resolveBackendError(error);
    if (payload?.messageId) {
        return localizedText(payload.messageId);
    }
    if (payload?.message) {
        return payload.message;
    }
    return localizedText(GENERIC_FALLBACK_ID);
};
